import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { question: "", answer: "", order: 0 };

export default function AdminFAQ() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/faq`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm(f); setModalOpen(true); };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editing) await axios.put(`${API}/admin/faq/${editing.id}`, payload);
      else await axios.post(`${API}/admin/faq`, payload);
      toast.success("FAQ berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/faq/${deleteId}`);
      toast.success("FAQ berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-faq-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">FAQ</h1>
          <p className="text-sm text-[#64748B]">Kelola pertanyaan yang sering diajukan.</p>
        </div>
        <Button data-testid="admin-faq-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah FAQ</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={HelpCircle} title="Belum ada FAQ" description="Tambahkan pertanyaan yang sering diajukan." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-faq-table">
            <TableHeader><TableRow><TableHead>Pertanyaan</TableHead><TableHead>Urutan</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((f) => (
                <TableRow key={f.id} data-testid={`admin-faq-row-${f.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{f.question}</TableCell>
                  <TableCell className="text-[#64748B]">{f.order}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`admin-faq-edit-${f.id}`} onClick={() => openEdit(f)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-faq-delete-${f.id}`} onClick={() => setDeleteId(f.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-faq-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit FAQ" : "Tambah FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Pertanyaan</Label><Input data-testid="faq-form-question" value={form.question} onChange={(e) => update("question", e.target.value)} /></div>
            <div><Label>Jawaban</Label><Textarea data-testid="faq-form-answer" value={form.answer} onChange={(e) => update("answer", e.target.value)} rows={3} /></div>
            <div><Label>Urutan</Label><Input data-testid="faq-form-order" type="number" value={form.order} onChange={(e) => update("order", e.target.value)} /></div>
            <Button data-testid="admin-faq-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus FAQ?" onConfirm={handleDelete} testId="admin-faq-delete-confirm" />
    </div>
  );
}
